import { supabase } from '@/lib/supabase'
import type {
  Notification,
  NotificationFilters,
  NotificationStats,
  CreateNotificationData,
} from '@/types/notification'

// STORY-033: Service de gestion des notifications
export const notificationsService = {
  /**
   * Recuperer toutes les notifications
   */
  async getAll(filters?: NotificationFilters): Promise<Notification[]> {
    let query = supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })

    if (filters?.type) {
      query = query.eq('type', filters.type)
    }

    if (filters?.read !== undefined) {
      query = query.eq('read', filters.read)
    }

    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo)
    }

    if (filters?.limit) {
      query = query.limit(filters.limit)
    }

    const { data, error } = await query

    if (error) throw error
    return data as Notification[]
  },

  /**
   * Recuperer les notifications non lues
   */
  async getUnread(limit = 10): Promise<Notification[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('read', false)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data as Notification[]
  },

  /**
   * Obtenir les statistiques des notifications
   */
  async getStats(): Promise<NotificationStats> {
    const { data, error } = await supabase
      .from('notifications')
      .select('read')

    if (error) throw error

    const notifications = data || []

    return {
      total: notifications.length,
      unread: notifications.filter(n => !n.read).length,
    }
  },

  /**
   * Compter les notifications non lues
   */
  async countUnread(): Promise<number> {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('read', false)

    if (error) throw error
    return count ?? 0
  },

  /**
   * Creer une notification
   */
  async create(data: CreateNotificationData): Promise<Notification> {
    const { data: notification, error } = await supabase
      .from('notifications')
      .insert({
        type: data.type,
        title: data.title || null,
        message: data.message,
        link: data.link || null,
        user_id: data.user_id || null,
        read: false,
      })
      .select()
      .single()

    if (error) throw error
    return notification as Notification
  },

  /**
   * Marquer une notification comme lue
   */
  async markAsRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id)

    if (error) throw error
  },

  /**
   * Marquer toutes les notifications comme lues
   */
  async markAllAsRead(): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('read', false)

    if (error) throw error
  },

  /**
   * Supprimer une notification
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  /**
   * S'abonner aux nouvelles notifications (temps reel)
   */
  subscribeToNotifications(callback: (notification: Notification) => void) {
    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          callback(payload.new as Notification)
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  },
}

export default notificationsService
